import type { ComponentPropsWithoutRef } from 'react'
import classNames from 'classnames'
import styles from './styles/Icon.css'
import ICON_MAP from './icon-mapping'
import type { IconName } from './icon-mapping'

/**
 * Icon component using @central-icons-react
 *
 * Usage:
 *   <Icon name="chat" />
 *   <Icon name="table" size={20} />
 *   <ChatIcon size={16} color="#333" />
 */

// `name` stays a plain string rather than IconName: several callers resolve it from
// config at runtime, and an unmapped name is handled by the warn-and-render-nothing
// path below. Import IconName from ./icon-mapping to constrain such a config object.
// `size` takes CSS lengths as well as px numbers: icons migrated off the old font
// pass `1em` so they keep scaling with the font-size their stylesheet already sets.
export type IconProps = Omit<ComponentPropsWithoutRef<'svg'>, 'name'> & {
  name: string
  size?: number | string
}

export type NamedIconProps = Omit<IconProps, 'name'>

const isIconName = (name: string): name is IconName => name in ICON_MAP

// `color` is deliberately left undefined unless a caller asks for one: Central writes the
// prop straight into an inline `style`, which would outrank the colour any className sets.
// The paths stroke `currentColor`, so leaving it out inherits exactly the same by default.
const Icon = ({ name, size = 24, color, className, strokeWidth, ...rest }: IconProps) => {
  if (isIconName(name)) {
    const CentralComponent = ICON_MAP[name]
    return <CentralComponent size={size} color={color} className={classNames(styles.icon, className)} {...rest} />
  }

  console.warn(`Icon "${name}" not found.`)
  return null
}

// ============================================
// NAMED EXPORTS - for convenient imports
// ============================================

// Chat & Messaging
export const ChatIcon = (props: NamedIconProps) => <Icon name='chat' {...props} />
export const NewChatIcon = (props: NamedIconProps) => <Icon name='newChat' {...props} />

// Table & Data
export const TableIcon = (props: NamedIconProps) => <Icon name='table' {...props} />

// Actions
export const TrashIcon = (props: NamedIconProps) => <Icon name='trash' {...props} />
export const EditIcon = (props: NamedIconProps) => <Icon name='edit' {...props} />
export const EditBoxIcon = (props: NamedIconProps) => <Icon name='editBox' {...props} />
export const QuoteIcon = (props: NamedIconProps) => <Icon name='quote' {...props} />
export const ShareIcon = (props: NamedIconProps) => <Icon name='share' {...props} />
export const CopyIcon = (props: NamedIconProps) => <Icon name='copy' {...props} />
export const DownloadIcon = (props: NamedIconProps) => <Icon name='download' {...props} />
export const DownloadArrowIcon = (props: NamedIconProps) => <Icon name='downloadArrow' {...props} />
export const SearchIcon = (props: NamedIconProps) => <Icon name='search' {...props} />
export const PlusIcon = (props: NamedIconProps) => <Icon name='plus' {...props} />
export const MinusIcon = (props: NamedIconProps) => <Icon name='minus' {...props} />
export const XIcon = (props: NamedIconProps) => <Icon name='x' {...props} />
export const StopIcon = (props: NamedIconProps) => <Icon name='stop' {...props} />
export const CheckIcon = (props: NamedIconProps) => <Icon name='check' {...props} />
export const ExternalLinkIcon = (props: NamedIconProps) => <Icon name='externalLink' {...props} />

// UI Elements
export const HelpIcon = (props: NamedIconProps) => <Icon name='help' {...props} />
export const InfoIcon = (props: NamedIconProps) => <Icon name='info' {...props} />
export const WarningIcon = (props: NamedIconProps) => <Icon name='warning' {...props} />
export const SettingsIcon = (props: NamedIconProps) => <Icon name='settings' {...props} />
export const SparklesIcon = (props: NamedIconProps) => <Icon name='sparkles' {...props} />
export const MailIcon = (props: NamedIconProps) => <Icon name='mail' {...props} />

// Navigation
export const ChevronRightIcon = (props: NamedIconProps) => <Icon name='chevronRight' {...props} />
export const ChevronLeftIcon = (props: NamedIconProps) => <Icon name='chevronLeft' {...props} />
export const ChevronDownIcon = (props: NamedIconProps) => <Icon name='chevronDown' {...props} />
export const ChevronUpIcon = (props: NamedIconProps) => <Icon name='chevronUp' {...props} />
export const MinimizeIcon = (props: NamedIconProps) => <Icon name='minimize' {...props} />
export const MaximizeIcon = (props: NamedIconProps) => <Icon name='maximize' {...props} />

// Menus
export const MoreVerticalIcon = (props: NamedIconProps) => <Icon name='moreVertical' {...props} />
export const MoreHorizontalIcon = (props: NamedIconProps) => <Icon name='moreHorizontal' {...props} />

// People & roles
export const UserIcon = (props: NamedIconProps) => <Icon name='user' {...props} />
export const UsersIcon = (props: NamedIconProps) => <Icon name='users' {...props} />
export const UserPlusIcon = (props: NamedIconProps) => <Icon name='userPlus' {...props} />
export const UserRemoveIcon = (props: NamedIconProps) => <Icon name='userRemove' {...props} />
export const ShieldCheckIcon = (props: NamedIconProps) => <Icon name='shieldCheck' {...props} />
export const ClockIcon = (props: NamedIconProps) => <Icon name='clock' {...props} />

// Misc
export const BookmarkCheckIcon = (props: NamedIconProps) => <Icon name='bookmarkCheck' {...props} />
export const FolderIcon = (props: NamedIconProps) => <Icon name='folder' {...props} />
export const FolderAddRightIcon = (props: NamedIconProps) => <Icon name='folderAddRight' {...props} />
export const ArchiveIcon = (props: NamedIconProps) => <Icon name='archive' {...props} />
export const EyeIcon = (props: NamedIconProps) => <Icon name='eye' {...props} />
export const EyeOffIcon = (props: NamedIconProps) => <Icon name='eyeOff' {...props} />
export const TagIcon = (props: NamedIconProps) => <Icon name='tag' {...props} />
export const BellIcon = (props: NamedIconProps) => <Icon name='bell' {...props} />
export const LightbulbIcon = (props: NamedIconProps) => <Icon name='lightbulb' {...props} />
export const CalendarIcon = (props: NamedIconProps) => <Icon name='calendar' {...props} />
export const SearchChecklistIcon = (props: NamedIconProps) => <Icon name='searchChecklist' {...props} />
export const DashboardIcon = (props: NamedIconProps) => <Icon name='dashboardLayout' {...props} />
export const PatentsIcon = (props: NamedIconProps) => <Icon name='patents' {...props} />
export const JournalsIcon = (props: NamedIconProps) => <Icon name='journals' {...props} />
export const OrganizationsIcon = (props: NamedIconProps) => <Icon name='organizations' {...props} />
export const FundersIcon = (props: NamedIconProps) => <Icon name='funders' {...props} />
export const BrowserExtensionIcon = (props: NamedIconProps) => <Icon name='browserExtension' {...props} />
export const ApiIcon = (props: NamedIconProps) => <Icon name='api' {...props} />
export const McpIcon = (props: NamedIconProps) => <Icon name='mcp' {...props} />
export const BioIcon = (props: NamedIconProps) => <Icon name='bio' {...props} />
export const MetricsIcon = (props: NamedIconProps) => <Icon name='metrics' {...props} />
export const SubscriptionIcon = (props: NamedIconProps) => <Icon name='subscription' {...props} />
export const AccountSettingsIcon = (props: NamedIconProps) => <Icon name='accountSettings' {...props} />
export const SignOutIcon = (props: NamedIconProps) => <Icon name='signOut' {...props} />
export const ShareRightIcon = (props: NamedIconProps) => <Icon name='shareRight' {...props} />
export const ExportIcon = (props: NamedIconProps) => <Icon name='export' {...props} />
export const RegenerateIcon = (props: NamedIconProps) => <Icon name='regenerate' {...props} />
export const LayoutGridIcon = (props: NamedIconProps) => <Icon name='layoutGrid' {...props} />
export const BulletListIcon = (props: NamedIconProps) => <Icon name='bulletList' {...props} />
export const ZapIcon = (props: NamedIconProps) => <Icon name='zap' {...props} />

// Branding
interface SciteLogoProps {
  height?: number
  className?: string
}

export const SciteLogoSmall = ({ height = 16, className }: SciteLogoProps) => {
  const width = Math.round(height * (20 / 16))
  return (
    <svg
      width={width}
      height={height}
      viewBox='0 0 20 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      aria-label='scite'
    >
      <path
        d='M2.85 8.625C2.88333 9.10834 3.08333 9.54167 3.45 9.925C3.81667 10.3083 4.36667 10.5 5.1 10.5C5.65 10.5 6.05833 10.3833 6.325 10.15C6.59167 9.9 6.725 9.60833 6.725 9.275C6.725 8.99167 6.625 8.75 6.425 8.55C6.225 8.33333 5.88333 8.175 5.4 8.075L3.775 7.7C2.575 7.43333 1.7 6.975 1.15 6.325C0.6 5.65833 0.325 4.89167 0.325 4.025C0.325 3.475 0.441667 2.95833 0.675 2.475C0.908333 1.975 1.225 1.55 1.625 1.2C2.04167 0.833334 2.525 0.541667 3.075 0.325001C3.64167 0.108334 4.25833 0 4.925 0C5.79167 0 6.525 0.125001 7.125 0.375001C7.725 0.608334 8.20833 0.908334 8.575 1.275C8.95833 1.64167 9.24167 2.03333 9.425 2.45C9.60833 2.85 9.71667 3.21667 9.75 3.55L6.975 4.175C6.94167 3.99167 6.88333 3.80833 6.8 3.625C6.73333 3.425 6.61667 3.24167 6.45 3.075C6.28333 2.90833 6.075 2.775 5.825 2.675C5.59167 2.575 5.3 2.525 4.95 2.525C4.51667 2.525 4.14167 2.64167 3.825 2.875C3.525 3.10833 3.375 3.4 3.375 3.75C3.375 4.05 3.475 4.29167 3.675 4.475C3.89167 4.65833 4.16667 4.78333 4.5 4.85L6.25 5.225C7.46667 5.475 8.375 5.94167 8.975 6.625C9.59167 7.30833 9.9 8.1 9.9 9C9.9 9.5 9.8 9.99167 9.6 10.475C9.41667 10.9583 9.125 11.3917 8.725 11.775C8.325 12.1583 7.825 12.4667 7.225 12.7C6.625 12.9333 5.925 13.05 5.125 13.05C4.19167 13.05 3.40833 12.9167 2.775 12.65C2.14167 12.3833 1.625 12.0583 1.225 11.675C0.825 11.2917 0.525 10.8833 0.325 10.45C0.141667 10 0.0333333 9.6 0 9.25L2.85 8.625Z'
        fill='#0062FF'
      />
      <path d='M11.225 15.4V12.675H20V15.4H11.225Z' fill='#0062FF' />
    </svg>
  )
}

// Arrows
export const ArrowRightIcon = (props: NamedIconProps) => <Icon name='arrowRight' {...props} />
export const ArrowLeftIcon = (props: NamedIconProps) => <Icon name='arrowLeft' {...props} />
export const ArrowUpIcon = (props: NamedIconProps) => <Icon name='arrowUp' {...props} />
export const ArrowCircleRightIcon = (props: NamedIconProps) => <Icon name='arrowCircleRight' {...props} />
export const ArrowUpRightIcon = (props: NamedIconProps) => <Icon name='arrowUpRight' {...props} />

// Status
export const LockIcon = (props: NamedIconProps) => <Icon name='lock' {...props} />
export const CheckCircleIcon = (props: NamedIconProps) => <Icon name='checkCircle' {...props} />
export const TimesCircleIcon = (props: NamedIconProps) => <Icon name='timesCircle' {...props} />
export const PlusCircleIcon = (props: NamedIconProps) => <Icon name='plusCircle' {...props} />
export const ExclamationCircleIcon = (props: NamedIconProps) => <Icon name='exclamationCircle' {...props} />

// Other
export const FlagIcon = (props: NamedIconProps) => <Icon name='flag' {...props} />
export const VolumeUpIcon = (props: NamedIconProps) => <Icon name='volumeUp' {...props} />
export const MagicWandIcon = (props: NamedIconProps) => <Icon name='magicWand' {...props} />
export const CommentIcon = (props: NamedIconProps) => <Icon name='comment' {...props} />
export const LinkIcon = (props: NamedIconProps) => <Icon name='link' {...props} />
export const FilterIcon = (props: NamedIconProps) => <Icon name='filter' {...props} />
export const AdjustmentsIcon = (props: NamedIconProps) => <Icon name='adjustments' {...props} />
export const ColumnsIcon = (props: NamedIconProps) => <Icon name='columns' {...props} />

// Sidebar toggle (stateful)
export const SidebarToggleIcon = ({ expanded, ...props }: NamedIconProps & { expanded?: boolean }) => (
  <Icon name={expanded ? 'sidebarExpanded' : 'sidebarCollapsed'} {...props} />
)

// Admin & infrastructure
export const KeyIcon = (props: NamedIconProps) => <Icon name='key' {...props} />
export const ServerIcon = (props: NamedIconProps) => <Icon name='server' {...props} />
export const UnlockIcon = (props: NamedIconProps) => <Icon name='unlock' {...props} />
export const GaugeIcon = (props: NamedIconProps) => <Icon name='gauge' {...props} />
export const ChecklistIcon = (props: NamedIconProps) => <Icon name='checklist' {...props} />
export const SealIcon = (props: NamedIconProps) => <Icon name='seal' {...props} />
export const ShieldIcon = (props: NamedIconProps) => <Icon name='shield' {...props} />
export const PaperPlaneIcon = (props: NamedIconProps) => <Icon name='paperPlane' {...props} />
export const ImageIcon = (props: NamedIconProps) => <Icon name='image' {...props} />
export const BookIcon = (props: NamedIconProps) => <Icon name='book' {...props} />
export const FileTextIcon = (props: NamedIconProps) => <Icon name='fileText' {...props} />

// Media controls
export const PlayIcon = (props: NamedIconProps) => <Icon name='play' {...props} />
export const PauseIcon = (props: NamedIconProps) => <Icon name='pause' {...props} />

// Social
export const TwitterIcon = (props: NamedIconProps) => <Icon name='twitter' {...props} />
export const LinkedinIcon = (props: NamedIconProps) => <Icon name='linkedin' {...props} />
export const FacebookIcon = (props: NamedIconProps) => <Icon name='facebook' {...props} />

// Menus & sorting
export const MenuIcon = (props: NamedIconProps) => <Icon name='menu' {...props} />
export const SortArrowsIcon = (props: NamedIconProps) => <Icon name='sortArrows' {...props} />

// Arrows & chevrons
export const ArrowDownIcon = (props: NamedIconProps) => <Icon name='arrowDown' {...props} />
export const ChevronDoubleLeftIcon = (props: NamedIconProps) => <Icon name='chevronDoubleLeft' {...props} />
export const ChevronDoubleRightIcon = (props: NamedIconProps) => <Icon name='chevronDoubleRight' {...props} />
export const LevelUpIcon = (props: NamedIconProps) => <Icon name='levelUp' {...props} />
export const EnterIcon = (props: NamedIconProps) => <Icon name='enter' {...props} />

// Graphs & structure
export const RobotIcon = (props: NamedIconProps) => <Icon name='robot' {...props} />
export const NetworkIcon = (props: NamedIconProps) => <Icon name='network' {...props} />
export const HierarchyIcon = (props: NamedIconProps) => <Icon name='hierarchy' {...props} />
export const MinusCircleIcon = (props: NamedIconProps) => <Icon name='minusCircle' {...props} />

export default Icon
